import { Reorder } from 'framer-motion'
import { Images, ImageUp, Star, Upload, X } from 'lucide-react'
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as m from '~/paraglide/messages'
import { log } from '~/shared/lib/logger'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { AssetLibraryPicker } from './asset-library-picker'

export interface ImageFile {
  file: File
  preview: string
  id: string
}

type UnifiedImage = {
  id: string
  preview: string
  isExisting: boolean
  file?: File
  // preview object URL created by this component (revoke on unmount); false for
  // remote URLs and parent-owned initialNewImages previews.
  owned?: boolean
}

function getNewImageFiles(images: UnifiedImage[]): ImageFile[] {
  return images.flatMap((img) =>
    img.isExisting || !img.file ? [] : [{ file: img.file, preview: img.preview, id: img.id }],
  )
}

function stagedImage(file: File): UnifiedImage {
  return {
    id: crypto.randomUUID(),
    preview: URL.createObjectURL(file),
    isExisting: false,
    file,
    owned: true,
  }
}

function getInsertPositions(images: UnifiedImage[], newImageFiles: ImageFile[]): number[] {
  return newImageFiles.map((newImg) => images.findIndex((img) => img.id === newImg.id))
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024
// Backend (validateImageUpload) accepts JPEG/PNG/WebP by magic bytes, so
// reject anything else here rather than letting the upload 400 later.
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

function isAcceptedImageFile(file: File): boolean {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    toast.error(m.unsupportedImageFileSkipped({ fileName: file.name }))
    return false
  }
  if (file.size > MAX_IMAGE_BYTES) {
    toast.error(m.fileIsTooLargeMaximumSizeIs5MB({ fileName: file.name }))
    return false
  }
  return true
}

export interface ExistingImage {
  id: string
  url: string
}

export interface CoverControls {
  preview?: string
  file?: File
  put: (file: File) => void
}

interface ImagesSectionProps {
  title: string
  description: string
  entityId?: string
  existingImages?: ExistingImage[]
  /** Pre-fetched File objects to seed as new (non-existing) images on mount. Used by copy-event flow. */
  initialNewImages?: ImageFile[]
  onImagesChange?: (files: ImageFile[], primaryIndex: number, insertPositions?: number[]) => void
  onDeleteExisting?: (imageId: string) => Promise<void>
  onReorderExisting?: (imageIds: string[]) => Promise<void>
  /** Saved images the person removed whose deletion has to wait for the save, because a staged image replaces them. */
  onRemovedExistingChange?: (imageIds: string[]) => void
  /** Extra add-image actions rendered beside the library picker; `addImage` appends a file, `cover.put` puts one in front as the new cover. */
  actions?: (addImage: (file: File) => void, cover: CoverControls) => React.ReactNode
}

export function ImagesSection({
  title,
  description,
  entityId,
  existingImages = [],
  initialNewImages = [],
  onImagesChange,
  onDeleteExisting,
  onReorderExisting,
  onRemovedExistingChange,
  actions,
}: ImagesSectionProps) {
  const [allImages, setAllImages] = useState<UnifiedImage[]>(() => [
    ...existingImages.map((img) => ({
      id: img.id,
      preview: img.url,
      isExisting: true,
    })),
    ...initialNewImages.map((img) => ({
      id: img.id,
      preview: img.preview,
      isExisting: false,
      file: img.file,
    })),
  ])

  useEffect(() => {
    if (!initialNewImages.length || !onImagesChange) return
    onImagesChange(initialNewImages, 0)
  }, [initialNewImages, onImagesChange])
  const [deletingImage, setDeletingImage] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [removedExisting, setRemovedExisting] = useState<UnifiedImage[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)

  const reorderTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingReorderRef = useRef<UnifiedImage[] | null>(null)
  const previousOrderRef = useRef<UnifiedImage[]>(allImages)
  const allImagesRef = useRef<UnifiedImage[]>(allImages)

  useEffect(() => {
    allImagesRef.current = allImages
  }, [allImages])

  useEffect(() => {
    return () => {
      if (reorderTimeoutRef.current) {
        clearTimeout(reorderTimeoutRef.current)
      }
      for (const img of allImagesRef.current) {
        if (img.owned) URL.revokeObjectURL(img.preview)
      }
    }
  }, [])

  const commitImages = (updatedImages: UnifiedImage[]) => {
    setAllImages(updatedImages)
    const newImageFiles = getNewImageFiles(updatedImages)
    onImagesChange?.(newImageFiles, 0, getInsertPositions(updatedImages, newImageFiles))
  }

  const commitRemovedExisting = (removed: UnifiedImage[]) => {
    setRemovedExisting(removed)
    onRemovedExistingChange?.(removed.map((img) => img.id))
  }

  const handleFiles = (files: FileList | null) => {
    if (!files) return

    const newUnifiedImages: UnifiedImage[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      if (!isAcceptedImageFile(file)) continue
      if (allImages.length + newUnifiedImages.length >= 10) {
        toast.error(m.maximum10FilesAllowed())
        break
      }

      const preview = URL.createObjectURL(file)
      newUnifiedImages.push({
        id: `${Date.now()}-${i}`,
        preview,
        isExisting: false,
        file,
        owned: true,
      })
    }

    if (newUnifiedImages.length > 0) commitImages([...allImages, ...newUnifiedImages])
  }

  const addLibraryImage = async (uploadable: File) => {
    if (uploadable.size > MAX_IMAGE_BYTES) {
      toast.error(m.fileIsTooLargeMaximumSizeIs5MB({ fileName: uploadable.name }))
      return
    }

    // Read the freshest committed list from the ref rather than the `allImages`
    // closure, which can be stale across the picker's async pick flow.
    if (allImagesRef.current.length >= 10) {
      toast.error(m.maximum10FilesAllowed())
      return
    }

    commitImages([...allImagesRef.current, stagedImage(uploadable)])
  }

  const putCover = (file: File) => {
    if (allImagesRef.current.length >= 10) {
      toast.error(m.maximum10FilesAllowed())
      return
    }
    commitImages([stagedImage(file), ...allImagesRef.current])
  }

  const handleReplaceCover = (file: File) => {
    if (!isAcceptedImageFile(file)) return
    const [cover, ...rest] = allImagesRef.current
    if (cover?.isExisting) commitRemovedExisting([...removedExisting, cover])
    else if (cover?.owned) URL.revokeObjectURL(cover.preview)
    commitImages([stagedImage(file), ...rest])
  }

  const handleReplaceInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleReplaceCover(file)
    if (replaceInputRef.current) replaceInputRef.current.value = ''
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDeleteExisting = async (imageId: string) => {
    const image = allImages.find((img) => img.id === imageId)
    if (!image) return
    const othersSaved = allImages.some((img) => img.isExisting && img.id !== imageId)
    const replacementStaged = allImages.some((img) => !img.isExisting)
    if (!othersSaved && replacementStaged) {
      commitRemovedExisting([...removedExisting, image])
      commitImages(allImages.filter((img) => img.id !== imageId))
      toast.info(m.imagesRemovedOnSave())
      return
    }

    if (!entityId || !onDeleteExisting) return
    if (!confirm(m.areYouSureYouWantToDeleteThisImage())) return

    setDeletingImage(imageId)
    try {
      await onDeleteExisting(imageId)
      commitImages(allImagesRef.current.filter((img) => img.id !== imageId))
      toast.success(m.imageDeletedSuccessfully())
    } catch (error) {
      log.error(error instanceof Error ? error : new Error(String(error)), 'Failed to delete image')
      toast.error(m.failedToDeleteImage(), {
        description: error instanceof Error ? error.message : m.pleaseTryAgain(),
      })
    } finally {
      setDeletingImage(null)
    }
  }

  const handleDeleteNew = (id: string) => {
    const imageToRemove = allImages.find((img) => img.id === id)
    if (imageToRemove?.owned) {
      URL.revokeObjectURL(imageToRemove.preview)
    }

    const remaining = allImages.filter((img) => img.id !== id)
    if (removedExisting.length > 0 && remaining.every((img) => img.isExisting)) {
      commitImages([...removedExisting, ...remaining])
      commitRemovedExisting([])
      return
    }
    commitImages(remaining)
  }

  const debouncedReorderAPI = useCallback(
    async (reorderedImages: UnifiedImage[], previousOrder: UnifiedImage[]) => {
      const newOrderExisting = reorderedImages.filter((img) => img.isExisting).map((img) => img.id)
      const oldOrderExisting = previousOrder.filter((img) => img.isExisting).map((img) => img.id)

      const existingOrderChanged =
        newOrderExisting.length > 0 &&
        (newOrderExisting.length !== oldOrderExisting.length ||
          newOrderExisting.some((url, index) => url !== oldOrderExisting[index]))

      if (existingOrderChanged && onReorderExisting && entityId) {
        try {
          await onReorderExisting(newOrderExisting)
          toast.success(m.imageOrderUpdated())
        } catch (error) {
          log.error(
            error instanceof Error ? error : new Error(String(error)),
            'Failed to reorder images',
          )
          toast.error(m.failedToUpdateImageOrder(), {
            description: error instanceof Error ? error.message : m.pleaseTryAgain(),
          })
          setAllImages(previousOrder)
        }
      }
    },
    [onReorderExisting, entityId],
  )

  const handleReorder = (reorderedImages: UnifiedImage[]) => {
    if (reorderTimeoutRef.current) {
      clearTimeout(reorderTimeoutRef.current)
    } else {
      previousOrderRef.current = allImages
    }

    setAllImages(reorderedImages)

    const newImageFiles = getNewImageFiles(reorderedImages)
    const insertPositions = getInsertPositions(reorderedImages, newImageFiles)

    let primaryIndex = 0
    if (reorderedImages.length > 0 && newImageFiles.length > 0) {
      const primaryImage = reorderedImages[0]
      if (primaryImage.isExisting) {
        primaryIndex = 0
      } else {
        primaryIndex = newImageFiles.findIndex((img) => img.id === primaryImage.id)
        if (primaryIndex === -1) primaryIndex = 0
      }
    }

    if (onImagesChange) {
      onImagesChange(newImageFiles, primaryIndex, insertPositions)
    }

    pendingReorderRef.current = reorderedImages

    reorderTimeoutRef.current = setTimeout(() => {
      if (pendingReorderRef.current) {
        debouncedReorderAPI(pendingReorderRef.current, previousOrderRef.current)
        pendingReorderRef.current = null
        reorderTimeoutRef.current = null
      }
    }, 500)
  }

  const handleSetPrimary = async (clickedIndex: number) => {
    const previousOrder = allImages

    const newOrder = [...allImages]
    const [selectedImage] = newOrder.splice(clickedIndex, 1)
    newOrder.unshift(selectedImage)

    setAllImages(newOrder)

    const newImageFiles = getNewImageFiles(newOrder)
    const insertPositions = getInsertPositions(newOrder, newImageFiles)

    if (onImagesChange) {
      onImagesChange(newImageFiles, 0, insertPositions)
    }

    const newOrderExisting = newOrder.filter((img) => img.isExisting).map((img) => img.id)
    const oldOrderExisting = previousOrder.filter((img) => img.isExisting).map((img) => img.id)

    const existingOrderChanged =
      newOrderExisting.length > 0 &&
      (newOrderExisting.length !== oldOrderExisting.length ||
        newOrderExisting.some((url, index) => url !== oldOrderExisting[index]))

    if (existingOrderChanged && onReorderExisting && entityId) {
      try {
        await onReorderExisting(newOrderExisting)
        toast.success(m.imageOrderUpdated())
      } catch (error) {
        log.error(
          error instanceof Error ? error : new Error(String(error)),
          'Failed to reorder images',
        )
        toast.error(m.failedToUpdateImageOrder(), {
          description: error instanceof Error ? error.message : m.pleaseTryAgain(),
        })
        setAllImages(previousOrder)
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* biome-ignore lint/a11y/useSemanticElements: div needed for drag-and-drop event support */}
        <div
          role="button"
          tabIndex={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative rounded-lg border-2 border-dashed p-8 transition-colors',
            isDragging && 'border-primary bg-primary/5',
            !isDragging && 'border-muted-foreground/25',
            'cursor-pointer hover:border-primary/50',
          )}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              fileInputRef.current?.click()
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleInputChange}
            className="hidden"
          />

          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{m.dragAndDropImagesHereOrClickToBrowse()}</p>
              <p className="text-xs text-muted-foreground">{m.maximum10FilesUpTo5MBEach()}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <AssetLibraryPicker
            onSelect={addLibraryImage}
            trigger={
              <Button type="button" variant="outline" size="sm">
                <Images className="h-4 w-4" />
                {m.chooseFromLibrary()}
              </Button>
            }
          />
          {actions?.(addLibraryImage, {
            preview: allImages[0]?.preview,
            file: allImages[0]?.file,
            put: putCover,
          })}
          {allImages.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => replaceInputRef.current?.click()}
            >
              <ImageUp className="h-4 w-4" />
              {m.imagesReplaceCover()}
            </Button>
          )}
          <input
            ref={replaceInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleReplaceInputChange}
            className="hidden"
          />
        </div>

        {allImages.length > 0 && (
          <div className="relative">
            <div className="overflow-x-auto overflow-y-hidden pb-2 scrollbar-gutter-stable">
              <Reorder.Group
                as="div"
                axis="x"
                values={allImages}
                onReorder={handleReorder}
                className="flex gap-3 pr-6"
              >
                {allImages.map((image, index) => (
                  <ImagePreviewItem
                    key={image.id}
                    image={image}
                    index={index}
                    deletingImage={deletingImage}
                    onSetPrimary={handleSetPrimary}
                    onDeleteExisting={handleDeleteExisting}
                    onDeleteNew={handleDeleteNew}
                  />
                ))}
              </Reorder.Group>
            </div>
            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none" />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ImagePreviewItemProps {
  image: UnifiedImage
  index: number
  deletingImage: string | null
  onSetPrimary: (index: number) => void
  onDeleteExisting: (imageId: string) => void
  onDeleteNew: (id: string) => void
}

function ImagePreviewItem({
  image,
  index,
  deletingImage,
  onSetPrimary,
  onDeleteExisting,
  onDeleteNew,
}: ImagePreviewItemProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartPos, setDragStartPos] = useState<{
    x: number
    y: number
  } | null>(null)

  const handlePointerDown = (e: React.PointerEvent) => {
    setDragStartPos({ x: e.clientX, y: e.clientY })
  }

  const handleClick = (e: React.MouseEvent) => {
    if (!dragStartPos) return

    const moved = Math.abs(e.clientX - dragStartPos.x) + Math.abs(e.clientY - dragStartPos.y)

    if (moved < 5 && index !== 0) {
      e.preventDefault()
      onSetPrimary(index)
    }

    setDragStartPos(null)
  }

  return (
    <Reorder.Item
      value={image}
      as="div"
      className={cn(
        'group relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 overflow-hidden rounded-lg border-2',
        index === 0 ? 'border-primary' : 'border-border hover:border-primary/50',
        'cursor-grab active:cursor-grabbing',
      )}
      whileDrag={{
        scale: 1.05,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        zIndex: 50,
      }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <img
        src={image.preview}
        alt={m.uploadedImageAlt({ index: index + 1 })}
        className="h-full w-full object-cover pointer-events-none select-none"
        draggable={false}
      />

      {!isDragging && index !== 0 && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 pointer-events-none" />
      )}

      {image.isExisting ? null : (
        <div className="pointer-events-none absolute bottom-1.5 left-1.5 rounded-full bg-card/90 px-1.5 py-0.5 font-medium text-[10px] text-foreground backdrop-blur-sm">
          {m.new()}
        </div>
      )}

      {index === 0 && (
        <div className="absolute left-1.5 top-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground pointer-events-none">
          {m.imagesCoverBadge()}
        </div>
      )}

      {!isDragging && index !== 0 && (
        <button
          type="button"
          title={m.imagesSetAsCover()}
          aria-label={m.imagesSetAsCover()}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSetPrimary(index)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
          className="absolute top-1.5 right-8 z-20 rounded-full bg-black/60 p-1 text-white opacity-0 transition-all duration-200 hover:bg-primary focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
        >
          <Star className="h-3 w-3" />
        </button>
      )}

      {!isDragging && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            image.isExisting ? onDeleteExisting(image.id) : onDeleteNew(image.id)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
          disabled={deletingImage === image.id}
          title={m.imagesDeleteImage()}
          aria-label={m.imagesDeleteImage()}
          className="absolute top-1.5 right-1.5 z-20 rounded-full bg-black/60 p-1 text-white opacity-0 transition-all duration-200 hover:bg-red-500 focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed pointer-coarse:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Reorder.Item>
  )
}
